/**
 *
 * Dialog Fenster für sogenannte "Third Row", also die Datenreihen, die wahrscheinlich niemand benutzen will
 * Achtung: Infinite Scroll wird von Ionic React noch nicht supported, daher funktioniert es nicht einwandfrei
 *
 */

import React, { useState, useEffect, useRef } from "react";
import { IonModal, IonButton, IonHeader, IonTitle, IonToolbar, IonCheckbox, IonContent, IonButtons, IonIcon, IonInfiniteScroll, IonItem, IonLabel, IonSearchbar, IonGrid, IonRow, IonCol, IonFooter } from "@ionic/react";
import { closeOutline } from 'ionicons/icons';

import "../Statistics.css";
import http from "../../../utils/http-common";

type StatisticsModalProps = {
    id: number,
    name: string,
    isOpen: boolean,
    callback: () => void,
    checked: object,
    setChecked: (checked: object) => void,
    statisticId: string | number,
}

const StatisticsModal: React.FC<StatisticsModalProps> = ({ id, name, isOpen, callback, checked, setChecked, statisticId }: StatisticsModalProps) => {
    const [items, setItems] = useState([]);
    const [disableInfiniteScroll, setDisableInfiniteScroll] = useState<boolean>(false);
    const amount = 25;
    const [page, setPage] = useState<number>(1);
    const [searchStr, setSearchStr] = useState<string>("");
    const [finalSearch, setFinalSearch] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const debounceTimer = useRef<NodeJS.Timeout | null>(null);

    // Debounce für die Suche bei Tasteneingabe
    useEffect(() => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }

        debounceTimer.current = setTimeout(() => {
            setFinalSearch(searchStr);
        }, 300); // 300ms Verzögerung

        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, [searchStr]);

    useEffect(() => {
        if (id && name && statisticId) {
            setPage(1);
            fetchData(true);
        }
    }, [finalSearch, id, name, statisticId]);

    const fetchData = async (reset?: boolean) => {
        if (id && name && statisticId) {
            setLoading(true);
            const data = reset ? [] : items;
            const currentPage = reset ? 1 : page;
            const url: string = finalSearch !== "" ? `/fetchStatisticEvenMoreFilter?statistic_id=${statisticId}&name=${name}&page=${currentPage}&search=${finalSearch}` :
                `/fetchStatisticEvenMoreFilter?statistic_id=${statisticId}&name=${name}&page=${currentPage}`

            await http
                .get(url)
                .then(function (response) {
                    if (response.data.data.length > 0) {
                        //console.log(response)
                        setItems([...data, ...response.data.data]);
                        setDisableInfiniteScroll(response.data.data.length < amount || response.data.total === amount)
                        if (reset) {
                            setPage(2);
                        } else {
                            setPage((p) => p + 1)
                        }
                    } else {
                        setItems([])
                        setDisableInfiniteScroll(true);
                    }
                })
                .catch(function (error) {
                    console.log(error);
                })
                .finally(() => setLoading(false))
        }

    }

    const handleCheckbox = (code, content, section) => {
        const codeValue = section ? `${section}:${code}` : code;
        if (!loading) {
            if (checked[name] && checked[name].some(e => e.id === codeValue)) {
                //remove
                setChecked((checked) => {
                    checked[name] = checked[name].filter(item => { return item.id !== codeValue })
                    return ({ ...checked })
                })
            } else {
                //add
                setChecked((checked) => {
                    checked[name].push({
                        id: codeValue,
                        name: content
                    })
                    return ({ ...checked })
                })
            }
        }
    }

    async function searchNext($event: CustomEvent<void>) {
        await fetchData();
        ($event.target as HTMLIonInfiniteScrollElement).complete();
    }

    const isChecked = (code, section) => {
        const codeValue = section ? `${section}:${code}` : code;
        if (name && checked[name]) {
            if (checked[name].some(e => e.id === codeValue)) {
                return true
            }
            return false
        } return false;
    }

    return (
        <IonModal isOpen={isOpen} className="customModal" /* onWillPresent={() => initialFetchData()} */>
            <IonHeader >
                <IonToolbar color="primary">
                    <IonTitle>Alle Datenreihen</IonTitle>
                    <IonButtons slot="end">
                        <IonButton onClick={callback}>
                            <IonIcon icon={closeOutline} slot="icon-only"></IonIcon>
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
                <IonToolbar>
                    <IonSearchbar value={searchStr} onIonInput={e => setSearchStr(e.detail.value!)} placeholder="Suche"></IonSearchbar>
                </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding">
                <IonGrid className="noPaddingGrid">
                    {items.length > 0 ?
                        <IonRow>
                            {items.map((item, index) => (
                                <IonCol size="12" key={index} className="checkboxCol">
                                    <IonItem lines="none" className="checkboxItemModal ion-no-padding">
                                        <IonLabel className="ion-text-wrap" style={{ paddingTop: 4 }}>{item.content}</IonLabel>
                                        <IonCheckbox
                                            color="secondary"
                                            slot="start"
                                            value={item.code}
                                            checked={isChecked(item.code, item.section)}
                                            /* checked={checked[name] && checked[name].some(e => e.id === item.code)} */
                                            className="checkbox"
                                            onIonChange={() => handleCheckbox(item.code, item.content, item.section)}
                                        />
                                        <IonLabel className="ion-text-wrap" style={{ paddingTop: 4 }} slot="end">{item.section ? `${item.section}:${item.code}` : item.code}</IonLabel>
                                    </IonItem>
                                </IonCol>

                            ))}
                        </IonRow> : "Keine Datenreihen gefunden."}
                </IonGrid>
                <div>
                    <IonInfiniteScroll threshold="100px" disabled={disableInfiniteScroll} onIonInfinite={(e: CustomEvent<void>) => searchNext(e)}>
                        <div style={{ textAlign: "center", padding: "15px 0", opacity: 0.5 }}>Weitere Datenreihen werden geladen...</div>
                    </IonInfiniteScroll>
                </div>
            </IonContent>


            <IonFooter>
                <IonToolbar>
                    <IonButtons slot="primary">
                        <IonButton onClick={callback}>Schließen</IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonFooter>
        </IonModal>
    )
}

export default StatisticsModal;
